import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, FastField } from 'formik';
import { Col, Row, Button, ConfigProvider, Divider, Input } from 'antd';
import { UserOutlined, PhoneOutlined, MobileOutlined } from '@ant-design/icons';
import { Routes } from '@/routes';
import { useDevice, useThemeToken } from '@/hooks';
import { DormitoryValidation } from '@/validations';
import { useDormitoryLeaveForm, useUserDormitory } from '@/hooks/app';
import { Flex, LoadingSpin, PageContainer, InputCalendar, CalendarDate } from '@/components';
import { ReactComponent as HouseSVG } from '../house.svg';

const defaultForm = {
  fromDate: {} as CalendarDate,
  toDate: {} as CalendarDate,
  relation: '',
  address: '',
  status: '',
  description: '',
  mobileNumber: '',
  phoneNumber: '',
};

function DormitoryLeave() {

  const theme = useThemeToken();

  const navigate = useNavigate();
  const [t] = useTranslation(['common', 'dormitory']);
  const { isDesktop } = useDevice();
  const { dormitory, loading: dormitoryLoading } = useUserDormitory();
  const { error, loading, submit } = useDormitoryLeaveForm();

  const handleSubmit = useCallback(async ({ fromDate, toDate, ...rest }: (typeof defaultForm)) => {
    await submit({ ...rest, toDate: toDate.iso, fromDate: fromDate.iso });
    navigate(Routes.Dormitory);
  }, []);

  const pageTitle = t('common:features.dormitory-leave-registeration');

  return (
    <PageContainer displayFlex title={pageTitle}>
      <ConfigProvider componentSize={isDesktop() ? 'large' : 'middle'}>
        <Formik
          validateOnMount
          validationSchema={DormitoryValidation.leave}
          onSubmit={handleSubmit}
          initialValues={{ ...defaultForm }}
        >
          {(formik) => {
            return (
              <Form onSubmit={formik.submitForm}>
                <Row justify="center">
                  <Col xxl={8} xl={10} lg={12} md={14} xs={24} span={24}>
                    <Row justify="center" gutter={[16, 16]}>
                      <Col span={24}>
                        <Flex
                          direction="row"
                          align="center"
                          justify="flex-start"
                          className="rounded-xl text-dormitory bg-dormitory-lighter py-4 px-5 min-h-[8rem]"
                        >
                          {dormitoryLoading ? <LoadingSpin className="ms-0" /> : (
                            <>
                              <HouseSVG className="anim-look-around" height="100" />
                              <Flex className="ms-2" direction="column">
                                <span className="text-lg font-medium">
                                  {t('dormitory:labels.dormitory')}
                                  {dormitory?.dormitoryRoom?.dormitory?.name}
                                </span>
                                <span className="text-base font-medium">
                                  {t('dormitory:labels.block')}
                                  {dormitory?.dormitoryBlock?.name}
                                </span>
                              </Flex>
                            </>
                          )}
                        </Flex>
                      </Col>
                      <Col lg={12} span={12}>
                        <FastField
                          name="fromDate"
                          placeholder={t('common:labels.start-date')}
                          className="text-center"
                          onChange={(date) => formik.setFieldValue('fromDate', date)}
                          popoverProps={{ placement: isDesktop() ? 'bottom' : 'bottomLeft' }}
                          component={InputCalendar}
                        />
                      </Col>
                      <Col lg={12} span={12}>
                        <FastField
                          name="toDate"
                          className="text-center"
                          placeholder={t('common:labels.end-date')}
                          onChange={(date) => formik.setFieldValue('toDate', date)}
                          popoverProps={{ placement: isDesktop() ? 'bottom' : 'bottomRight' }}
                          component={InputCalendar}
                        />
                      </Col>
                      <Col span={24}>
                        <Divider className="!m-0" />
                      </Col>
                      <Col lg={24} span={24}>
                        <FastField
                          id="relation"
                          name="relation"
                          placeholder={t('dormitory:labels.relation')}
                          onChange={debounce(formik.handleChange, 600)}
                          suffix={<UserOutlined style={{ color: theme.colorPrimary }} />}
                          component={Input} />
                      </Col>
                      <Col lg={0} span={0}>
                      </Col>
                      <Col lg={12} span={24}>
                        <FastField
                          placeholder={t('dormitory:labels.phone-number')}
                          id="mobileNumber"
                          name="mobileNumber"
                          onChange={debounce(formik.handleChange, 600)}
                          suffix={<MobileOutlined style={{ color: theme.colorPrimary }} />}
                          component={Input} />
                      </Col>
                      <Col lg={12} span={24}>
                        <FastField
                          id="phoneNumber"
                          name="phoneNumber"
                          placeholder={t('dormitory:labels.landline-phone')}
                          onChange={debounce(formik.handleChange, 600)}
                          suffix={<PhoneOutlined style={{ color: theme.colorPrimary }} />}
                          component={Input}
                        />
                      </Col>
                      <Col span={24}>
                        <FastField
                          rows={2}
                          id="address"
                          name="address"
                          placeholder={t('dormitory:labels.address')}
                          component={Input.TextArea}
                          onChange={debounce(formik.handleChange, 300)}
                        />
                      </Col>
                      <Col span={24}>
                        <FastField
                          rows={3}
                          id="description"
                          name="description"
                          placeholder={t('common:labels.description')}
                          component={Input.TextArea}
                          onChange={debounce(formik.handleChange, 600)}
                        />
                      </Col>
                      <Col span={24}>
                        {error && <span className="text-red-500">{error}</span>}
                        <Flex justify="flex-end">
                          <Button
                            type="primary"
                            loading={loading}
                            className="w-full lg:w-auto"
                            onClick={formik.submitForm}
                            disabled={!!Object.keys(formik.errors).length}>
                            {t('dormitory:labels.leave-registeration')}
                          </Button>
                        </Flex>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Form>
            );
          }}
        </Formik>
      </ConfigProvider>
    </PageContainer>
  );
}

export default DormitoryLeave;
